import type { ClientEnvironment } from '@polyai/common/constants/global.constants';

import ENDPOINTS from 'api/endpoints';
import { doDelete, doGet, doPost, doPut } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';

export type MessagingHandoffProvider = 'nice' | 'zendesk';

export type MessagingHandoffSecret = Partial<
  Record<ClientEnvironment, Record<string, string>>
>;

const version = 'v2' as const;

export const getMessagingHandoffSecret = ({
  accountId,
  projectId,
  provider,
}: {
  accountId: string;
  projectId: string;
  provider: MessagingHandoffProvider;
}) => {
  return doGet<MessagingHandoffSecret>(
    generateNextPath(ENDPOINTS.MESSAGING_HANDOFF_INTEGRATIONS, {
      accountId,
      projectId,
      provider,
    }),
    LOGGER_TYPE.JUPITER,
    { version, disableRetry: true },
  );
};

export const upsertMessagingHandoffSecret = ({
  accountId,
  projectId,
  provider,
  clientEnv,
  data,
}: {
  accountId: string;
  projectId: string;
  provider: MessagingHandoffProvider;
  clientEnv: ClientEnvironment;
  data: Record<string, string>;
}) => {
  return doPut(
    generateNextPath(ENDPOINTS.MESSAGING_HANDOFF_INTEGRATION_ENV, {
      accountId,
      projectId,
      provider,
      clientEnv,
    }),
    LOGGER_TYPE.JUPITER,
    data,
    { version },
  );
};

export const deleteAllMessagingHandoffSecrets = ({
  accountId,
  projectId,
  provider,
}: {
  accountId: string;
  projectId: string;
  provider: MessagingHandoffProvider;
}) => {
  return doDelete(
    generateNextPath(ENDPOINTS.MESSAGING_HANDOFF_INTEGRATIONS, {
      accountId,
      projectId,
      provider,
    }),
    LOGGER_TYPE.JUPITER,
    {},
    { version },
  );
};

export const deleteMessagingHandoffSecret = ({
  accountId,
  projectId,
  provider,
  clientEnv,
}: {
  accountId: string;
  projectId: string;
  provider: MessagingHandoffProvider;
  clientEnv: ClientEnvironment;
}) => {
  return doDelete(
    generateNextPath(ENDPOINTS.MESSAGING_HANDOFF_INTEGRATION_ENV, {
      accountId,
      projectId,
      provider,
      clientEnv,
    }),
    LOGGER_TYPE.JUPITER,
    {},
    { version },
  );
};

// Create a NICE CXone DFO channel in the customer's tenant. Sourcerer
// combines the deployment-wide PolyAI app credentials (loaded server-side
// from the handoff-webhook secret) with the tenant's access key pair sent
// in the body. Returns the NICE-assigned channel record.
export const createNiceMessagingHandoffChannel = ({
  accountId,
  projectId,
  apiHost,
  channelIntegrationId,
  channelName,
  cxoneOauth,
  tokenUrl,
}: {
  accountId: string;
  projectId: string;
  apiHost: string;
  channelIntegrationId: string;
  channelName: string;
  cxoneOauth: {
    access_key_id: string;
    access_key_secret: string;
  };
  tokenUrl?: string;
}) => {
  return doPost<{ channel: { id?: string; [key: string]: unknown } }>(
    generateNextPath(ENDPOINTS.INTEGRATIONS_NICE_MESSAGING_HANDOFF_CHANNEL, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.AGENT_STREAM,
    {
      api_host: apiHost,
      channel_integration_id: channelIntegrationId,
      channel_name: channelName,
      cxone_oauth: cxoneOauth,
      ...(tokenUrl ? { token_url: tokenUrl } : {}),
    },
    { version: 'v1', prefix: 'sourcerer', disableRetry: true },
  );
};

// Delete a NICE CXone DFO channel from the customer's tenant. Called on
// disconnect with the stored access key so the channel is removed from
// NICE Admin, not just from PolyAI's secret store.
export const deleteNiceMessagingHandoffChannel = ({
  accountId,
  projectId,
  apiHost,
  channelId,
  cxoneOauth,
  tokenUrl,
}: {
  accountId: string;
  projectId: string;
  apiHost: string;
  channelId: string;
  cxoneOauth: {
    access_key_id: string;
    access_key_secret: string;
  };
  tokenUrl?: string;
}) => {
  return doDelete(
    generateNextPath(ENDPOINTS.INTEGRATIONS_NICE_MESSAGING_HANDOFF_CHANNEL, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.AGENT_STREAM,
    {
      api_host: apiHost,
      channel_id: channelId,
      cxone_oauth: cxoneOauth,
      ...(tokenUrl ? { token_url: tokenUrl } : {}),
    },
    { version: 'v1', prefix: 'sourcerer', disableRetry: true },
  );
};

// Fetch the PolyAI-owned NICE integration-box credentials (Client ID + Client
// Secret) that the customer pastes into NICE Admin's BYOC form. These live
// in the handoff-webhook AWS secret; sourcerer reads them server-side.
export const getNiceIntegrationBoxCredentials = ({
  accountId,
  projectId,
}: {
  accountId: string;
  projectId: string;
}) => {
  return doGet<{ client_id: string; client_secret: string }>(
    generateNextPath(
      ENDPOINTS.INTEGRATIONS_NICE_MESSAGING_HANDOFF_BOX_CREDENTIALS,
      { accountId, projectId },
    ),
    LOGGER_TYPE.AGENT_STREAM,
    { version: 'v1', prefix: 'sourcerer', disableRetry: true },
  );
};
